import { useState, useRef, useEffect } from "react";
import DropdownDefaultBox from "src/components/atoms/dropdown/dropdown-default";
import ButtonPro, {
	ButtonVariant,
} from "src/components/atoms/primary-button/primary-button";
import InputBox from "src/components/atoms/v2/inputBox/inputBox";
import ImageUploadButton from "src/components/molecules/imageUploader/imageUploader";
import LoadingModal from "src/components/molecules/loading/LoadingModal";
import { IFileInfo, IPhoneNumber } from "src/types/models/apiModels";
import { useAuth } from "../../auth/auth-context";
import { CreateResellerViewModel } from "./reseller-viewmodel";
import CheckBox from "src/components/molecules/check-box/check-box";
import { IDropdownOptionProp } from "src/components/atoms/dropdown/dropdown-box";
import Modal from "src/components/molecules/modal/modal";
import { ErrorDisplay } from "src/components/atoms/error/error";
import PhoneNumberField from "src/components/molecules/phoneNumberField/PhoneNumberField";
import { useNavigate } from "react-router-dom";
import Logo from "src/components/molecules/logo/Logo";
import HyperLink from "src/components/atoms/hyper-link/hyper-link";

export type TCallRoutingOption = "PSTN" | "WEBRTC";

export interface IDemoNumber {
	number: string;
	routingOption: TCallRoutingOption;
}

export interface ICreateResellerRequest {
	canApproveBusiness?: boolean;
	canApproveNumbers?: boolean;
	name: string;
	logo?: IFileInfo;
	rootUserEmail: string;
	defaultCountryCode: string;
	enabledRoutingOptions: TCallRoutingOption[];
	demoNumbers: IDemoNumber[];
}

interface ICreateResellerProps {
	isStandAlone?: boolean;
}

const CreateReseller: React.FC<ICreateResellerProps> = ({
	isStandAlone = false,
}) => {
	const navigate = useNavigate();
	const { user, logout } = useAuth();
	const [resellerName, setResellerName] = useState("");
	const [ownerEmailAddress, setOwnerEmailAddress] = useState("");
	const [countryCode, setCountryCode] = useState("Australia");
	const [resellerLogo, setResellerLogo] = useState<IFileInfo>();
	const [products, setProducts] = useState<TCallRoutingOption[]>([
		"WEBRTC",
		"PSTN",
	]);

	const [webRTCNumber, setWebRTCNumber] = useState("");
	const [demoNumberWebRTC, setDemoNumberWebRTC] = useState<IPhoneNumber>({
		countryCode: "",
		number: "",
	});
	const [PSTNNumber, setPSTNNumber] = useState("");
	const [demoNumberPSTN, setDemoNumberPSTN] = useState<IPhoneNumber>({
		countryCode: "",
		number: "",
	});
	const [canApproveBusiness, setCanApproveBusiness] = useState(true);
	const [canApproveNumbers, setCanApproveNumbers] = useState(true);
	const [hasWebRTC, setHasWebRTC] = useState(true);
	const [hasPSTN, setHasPSTN] = useState(true);
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const [saved, setSaved] = useState(false);
	const [saveFailed, setSaveFailed] = useState(false);

	const viewModel = useRef<CreateResellerViewModel | null>(null);

	useEffect(() => {
		if (user && user.accessToken) {
			viewModel.current = new CreateResellerViewModel(user.accessToken);
		}
	}, []);

	const handleSubmit = async () => {
		setLoading(true);

		const request: ICreateResellerRequest = {
			canApproveBusiness: true,
			canApproveNumbers: true,
			name: resellerName,
			rootUserEmail: ownerEmailAddress,
			logo: resellerLogo,
			enabledRoutingOptions: products,
			defaultCountryCode: countryCode,
			demoNumbers: [
				hasWebRTC
					? { number: webRTCNumber, routingOption: "WEBRTC" }
					: null,
				hasPSTN ? { number: PSTNNumber, routingOption: "PSTN" } : null,
			].filter((item): item is IDemoNumber => item !== null), // Type assertion for filter
		};

		try {
			const result =
				await viewModel.current?.createResellerAsync(request);
			if (result) {
				setSaved(true);
			} else {
				setSaveFailed(true);
			}
		} catch (err) {
			setSaveFailed(true);
		} finally {
			setLoading(false);
			setResellerLogo(undefined);
			setResellerName("");
			setOwnerEmailAddress("");
			setPSTNNumber("");
			setWebRTCNumber("");
		}
	};

	const handleBackClick = async () => {
		navigate("/select-account");
	};

	const handleWebRTCChange = (value: boolean) => {
		setHasWebRTC(value);
		if (!value) {
			setWebRTCNumber("");
		}

		setProducts((prev) =>
			value ? [...prev, "WEBRTC"] : prev.filter((p) => p !== "WEBRTC")
		);
	};

	const handlePSTNChange = (value: boolean) => {
		setHasPSTN(value);
		if (!value) {
			setPSTNNumber("");
		}

		setProducts((prev) =>
			value ? [...prev, "PSTN"] : prev.filter((p) => p !== "PSTN")
		);
	};

	return (
		<>
			<div
				className={`${isStandAlone ? "bg-gradient-to-r from-gray-700 to-gray-900 h-screen p-12 flex flex-col items-center justify-center overflow-x-auto" : "flex flex-col items-center justify-center overflow-x-auto w-full"}`}
			>
				<div
					className={`bg-white flex flex-col items-center justify-center gap-4 p-6 ${isStandAlone ? "" : "w-full"}`}
				>
					{isStandAlone && (
						<>
							<div className="flex items-center justify-end w-full">
								<HyperLink
									href={""}
									onClick={logout}
									label={"logout"}
								></HyperLink>
							</div>
							<Logo></Logo>
						</>
					)}
					<div
						className={`flex flex-col items-center justify-center gap-6 p-6 ${isStandAlone ? "" : "w-full"}`}
					>
						<div
							className={`flex flex-col ${isStandAlone ? "items-center " : "items-center w-full"} justify-center gap-6 `}
						>
							{isStandAlone && (
								<span className="font-Poppins font-semibold text-2xl">
									Create a reseller account
								</span>
							)}
							<ImageUploadButton
								image={resellerLogo}
								onUploadSuccess={function (
									image: IFileInfo | undefined
								): void {
									setResellerLogo(image);
								}}
							></ImageUploadButton>
							<InputBox
								label={"Name"}
								type={"text"}
								value={resellerName}
								onChange={function (value: string): void {
									setResellerName(value);
								}}
							></InputBox>
							<InputBox
								label={"Organisation owner email address"}
								type={"email"}
								value={ownerEmailAddress}
								onChange={function (value: string): void {
									setOwnerEmailAddress(value);
								}}
								helpText="Owner will have to register their account with Secured calls to use the system"
							></InputBox>
							<div className="flex flex-col items-start justify-center gap-6 w-full">
								<div className="flex flex-col items-start justify-center gap-2 w-full">
									<span className="font-Poppins font-semibold text-md">
										Country
									</span>
									<DropdownDefaultBox
										defaultText={countryCode}
										options={[
											{ label: "Australia", value: "AU" },
											{ label: "India", value: "IN" },
										]}
										onSelected={function (
											selectedOption: IDropdownOptionProp
										): void {
											setCountryCode(
												selectedOption.value
											);
										}}
									></DropdownDefaultBox>
								</div>
								<div className="flex flex-col items-start justify-center gap-4 w-full">
									<span className="font-Poppins font-semibold text-md">
										Permissions
									</span>

									<div className="flex items-center justify-start gap-4 w-full">
										<CheckBox
											title="Can approve businesses"
											checked={canApproveBusiness}
											onChange={function (
												value: boolean
											): void {
												setCanApproveBusiness(value);
											}}
										></CheckBox>
										<CheckBox
											title="Can approve numbers"
											checked={canApproveNumbers}
											onChange={function (
												value: boolean
											): void {
												setCanApproveNumbers(value);
											}}
										></CheckBox>
									</div>
								</div>
								<div className="flex flex-col items-start justify-center gap-4 w-full">
									<span className="font-Poppins font-semibold text-md">
										Products to enable
									</span>
									<div className="flex items-center justify-start gap-4 w-full">
										<CheckBox
											title="WebRTC calling"
											checked={hasWebRTC}
											onChange={function (
												value: boolean
											): void {
												// setHasWebRTC(value);
												// if (!value) {
												//   setDemoNumberWebRTC("");
												// }
												handleWebRTCChange(value);
											}}
										></CheckBox>

										<CheckBox
											title="PSTN calling"
											checked={hasPSTN}
											onChange={function (
												value: boolean
											): void {
												// setHasPSTN(value);
												// if (!value) {
												//   setDemoNumberPSTN("");
												// }
												handlePSTNChange(value);
											}}
										></CheckBox>
									</div>
								</div>
								<div className="bg-sky-100 flex flex-col items-center justify-center gap-4 w-full">
									<span className="bg-sky-700 font-Poppins font-semibold text-md text-white text-center w-full p-4">
										Demo account
									</span>
									<div className="flex items-center justify-center gap-4 w-full p-4">
										{hasWebRTC && (
											<div className="flex flex-col items-center justify-center gap-2 w-full">
												<PhoneNumberField
													label="WebRTC Number"
													phoneNumber={
														demoNumberWebRTC
													}
													value={webRTCNumber}
													onChange={(
														phoneNumber,
														value
													) => setWebRTCNumber(value)}
													showCountry
												></PhoneNumberField>
											</div>
										)}
										{hasPSTN && (
											<div className="flex flex-col items-center justify-center gap-2 w-full">
												<PhoneNumberField
													label="PSTN Number"
													phoneNumber={demoNumberPSTN}
													value={PSTNNumber}
													onChange={(
														phoneNumber,
														value
													) => setPSTNNumber(value)}
												></PhoneNumberField>
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
						<div className="flex items-center justify-center gap-4 w-full">
							<ButtonPro
								disabled={!resellerName || !ownerEmailAddress}
								onClick={handleSubmit}
								label={"Submit"}
							></ButtonPro>
							{isStandAlone && (
								<ButtonPro
									onClick={handleBackClick}
									variant={ButtonVariant.Secondary}
									label={"Back"}
								></ButtonPro>
							)}
						</div>
					</div>
					<LoadingModal open={loading}></LoadingModal>
					{error && <ErrorDisplay error={error}></ErrorDisplay>}

					<Modal
						isOpen={saved}
						onClose={function (): void {
							setSaved(false);
						}}
					>
						<span className="font-Poppins font-semibold text-xl">
							Reseller created
						</span>
					</Modal>

					<Modal
						isOpen={saveFailed}
						onClose={function (): void {
							setSaveFailed(false);
						}}
					>
						<span className="font-Poppins font-semibold text-xl">
							Reseller could not be created.
						</span>
					</Modal>
				</div>
			</div>
		</>
	);
};

export default CreateReseller;
